import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Single confirmation-modal state machine for every impactful, side-effecting
 * action of the automation operations panel (replay, reconcile, reject, DLQ
 * transition, cadence transition). The reason is captured inside the modal,
 * never inline, so an operator must acknowledge the consequence.
 *
 * The page owns the executors and the permission guards and passes them in;
 * this composable only decides which copy to show and which executor to run.
 */
export const useAutomationOperationsConfirm = ({
  replayForm,
  canOperate,
  canRunReplay,
  canReconcile,
  canReviewApproval,
  executors,
}) => {
  const { t } = useI18n();

  const confirmState = ref({
    open: false,
    kind: '',
    title: '',
    description: '',
    consequence: '',
    confirmText: '',
    confirmVariant: 'danger',
    reasonRequired: true,
    context: null,
  });
  const isConfirmSubmitting = ref(false);

  const closeConfirm = () => {
    confirmState.value = { ...confirmState.value, open: false };
  };

  // Prevent double-submit: while a confirmation is in flight, ignore repeat
  // confirms and keep the modal open on failure so the operator can retry.
  const handleConfirm = async ({ reason } = {}) => {
    if (isConfirmSubmitting.value) return;
    const { kind, context } = confirmState.value;
    isConfirmSubmitting.value = true;
    try {
      let succeeded = false;
      if (kind === 'replay') {
        succeeded = await executors.replay(reason);
      } else if (kind === 'reconcile') {
        succeeded = await executors.reconcile(reason);
      } else if (kind === 'reject') {
        succeeded = await executors.reject(reason);
      } else if (kind === 'dlq_transition') {
        succeeded = await executors.dlqTransition(
          context.deadLetter,
          context.actionName,
          reason
        );
      } else if (kind === 'cadence_transition') {
        succeeded = await executors.cadenceTransition(
          context.enrollment,
          context.actionName,
          reason
        );
      }
      if (succeeded) closeConfirm();
    } finally {
      isConfirmSubmitting.value = false;
    }
  };

  const openReplayConfirm = () => {
    if (!canRunReplay.value) return;
    confirmState.value = {
      open: true,
      kind: 'replay',
      title: t('AUTOMATION.OPERATIONS.CONFIRM.REPLAY_TITLE'),
      description: t('AUTOMATION.OPERATIONS.CONFIRM.REPLAY_DESCRIPTION', {
        id: replayForm.value.executionId,
      }),
      consequence: t('AUTOMATION.OPERATIONS.CONFIRM.REPLAY_CONSEQUENCE'),
      confirmText: t('AUTOMATION.OPERATIONS.CONFIRM.REPLAY_CONFIRM'),
      confirmVariant: 'danger',
      reasonRequired: true,
      context: null,
    };
  };

  const openReconcileConfirm = () => {
    if (!canReconcile.value) return;
    confirmState.value = {
      open: true,
      kind: 'reconcile',
      title: t('AUTOMATION.OPERATIONS.CONFIRM.RECONCILE_TITLE'),
      description: t('AUTOMATION.OPERATIONS.CONFIRM.RECONCILE_DESCRIPTION', {
        state: replayForm.value.reconciliationState,
      }),
      consequence: t('AUTOMATION.OPERATIONS.CONFIRM.RECONCILE_CONSEQUENCE'),
      confirmText: t('AUTOMATION.OPERATIONS.CONFIRM.RECONCILE_CONFIRM'),
      confirmVariant: 'primary',
      reasonRequired: true,
      context: null,
    };
  };

  const openRejectConfirm = () => {
    if (!canReviewApproval.value) return;
    confirmState.value = {
      open: true,
      kind: 'reject',
      title: t('AUTOMATION.OPERATIONS.CONFIRM.REJECT_TITLE'),
      description: t('AUTOMATION.OPERATIONS.CONFIRM.REJECT_DESCRIPTION', {
        ref: replayForm.value.actionRef,
      }),
      consequence: t('AUTOMATION.OPERATIONS.CONFIRM.REJECT_CONSEQUENCE'),
      confirmText: t('AUTOMATION.OPERATIONS.CONFIRM.REJECT_CONFIRM'),
      confirmVariant: 'danger',
      reasonRequired: true,
      context: null,
    };
  };

  // Literal keys (not interpolated paths) keep the i18n extractor able to see
  // every string and avoid dynamic-key lint warnings.
  const dlqActionLabel = {
    acknowledge: () => t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.ACKNOWLEDGE'),
    retry: () => t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.RETRY'),
    skip: () => t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.SKIP'),
    compensate: () => t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.COMPENSATE'),
    close: () => t('AUTOMATION.OPERATIONS.DLQ.ACTIONS.CLOSE'),
  };
  const dlqActionConsequence = {
    acknowledge: () =>
      t('AUTOMATION.OPERATIONS.CONFIRM.TRANSITION_CONSEQUENCE_ACKNOWLEDGE'),
    retry: () =>
      t('AUTOMATION.OPERATIONS.CONFIRM.TRANSITION_CONSEQUENCE_RETRY'),
    skip: () => t('AUTOMATION.OPERATIONS.CONFIRM.TRANSITION_CONSEQUENCE_SKIP'),
    compensate: () =>
      t('AUTOMATION.OPERATIONS.CONFIRM.TRANSITION_CONSEQUENCE_COMPENSATE'),
    close: () =>
      t('AUTOMATION.OPERATIONS.CONFIRM.TRANSITION_CONSEQUENCE_CLOSE'),
  };

  const openTransitionConfirm = (deadLetter, actionName) => {
    if (!canOperate.value) return;
    const variant = actionName === 'acknowledge' ? 'primary' : 'danger';
    confirmState.value = {
      open: true,
      kind: 'dlq_transition',
      title: t('AUTOMATION.OPERATIONS.CONFIRM.TRANSITION_TITLE', {
        action: (dlqActionLabel[actionName] || dlqActionLabel.retry)(),
        id: deadLetter.id,
      }),
      description: t('AUTOMATION.OPERATIONS.CONFIRM.TRANSITION_DESCRIPTION'),
      consequence: (
        dlqActionConsequence[actionName] || dlqActionConsequence.retry
      )(),
      confirmText: t('AUTOMATION.OPERATIONS.CONFIRM.TRANSITION_CONFIRM'),
      confirmVariant: variant,
      reasonRequired: true,
      context: { deadLetter, actionName },
    };
  };

  const openCadenceTransitionConfirm = (enrollment, actionName) => {
    if (!canOperate.value) return;
    const isPause = actionName === 'pause';
    confirmState.value = {
      open: true,
      kind: 'cadence_transition',
      title: isPause
        ? t('AUTOMATION.OPERATIONS.CONFIRM.CADENCE_PAUSE_TITLE')
        : t('AUTOMATION.OPERATIONS.CONFIRM.CADENCE_RESUME_TITLE'),
      description: isPause
        ? t('AUTOMATION.OPERATIONS.CONFIRM.CADENCE_PAUSE_DESCRIPTION', {
            id: enrollment.id,
          })
        : t('AUTOMATION.OPERATIONS.CONFIRM.CADENCE_RESUME_DESCRIPTION', {
            id: enrollment.id,
          }),
      consequence: isPause
        ? t('AUTOMATION.OPERATIONS.CONFIRM.CADENCE_PAUSE_CONSEQUENCE')
        : t('AUTOMATION.OPERATIONS.CONFIRM.CADENCE_RESUME_CONSEQUENCE'),
      confirmText: isPause
        ? t('AUTOMATION.OPERATIONS.CONFIRM.CADENCE_PAUSE_CONFIRM')
        : t('AUTOMATION.OPERATIONS.CONFIRM.CADENCE_RESUME_CONFIRM'),
      confirmVariant: isPause ? 'danger' : 'primary',
      reasonRequired: true,
      context: { enrollment, actionName },
    };
  };

  return {
    confirmState,
    isConfirmSubmitting,
    closeConfirm,
    handleConfirm,
    openReplayConfirm,
    openReconcileConfirm,
    openRejectConfirm,
    openTransitionConfirm,
    openCadenceTransitionConfirm,
  };
};
